import { useQuery } from '@tanstack/react-query'
import { galleryService } from '@/lib/api/services/gallery'
import type { PaginationParams, GalleryFilterParams } from '@/lib/api/types'

// Query keys factory
export const galleryKeys = {
  all: ['galleries'] as const,
  folders: (folderType?: 'PHOTO' | 'VIDEO') => [...galleryKeys.all, 'folders', folderType] as const,
  searches: () => [...galleryKeys.all, 'search'] as const,
  search: (params: PaginationParams, query: string) =>
    [...galleryKeys.searches(), params, query] as const,
  searchFolder: (params: PaginationParams, query: string) =>
    [...galleryKeys.all, 'searchFolder', params, query] as const,
  filters: () => [...galleryKeys.all, 'filter'] as const,
  filter: (params: PaginationParams, filters: GalleryFilterParams) =>
    [...galleryKeys.filters(), params, filters] as const,
}

/**
 * Hook to fetch all folders
 * @param folderType - Optional filter: 'PHOTO' for photo galleries, 'VIDEO' for video galleries
 */
export const useGalleryFolders = (folderType?: 'PHOTO' | 'VIDEO') => {
  return useQuery({
    queryKey: galleryKeys.folders(folderType),
    queryFn: () => galleryService.getFolders(folderType),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to search photo folders by query
 */
export const useSearchPhotoFolders = (
  query: string,
  params: PaginationParams
) => {
  return useQuery({
    queryKey: galleryKeys.searchFolder(params, query ),
    queryFn: () => galleryService.searchPhotoFolders(query, params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: !!query, // Only run this query if there is a search term
  })
}

/**
 * Hook to search photo galleries with filters
 */
export const useSearchGalleries = (
  params: PaginationParams,
  query: string
) => {
  return useQuery({
    queryKey: galleryKeys.search(params, query),
    queryFn: () => galleryService.searchGalleries(params, query),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: !!query, // Only run this query if there is a search term
  })
}

/**
 * Hook to filter photo galleries with pagination
 */
export const useFilterGalleries = (
  params: PaginationParams,
  filters: GalleryFilterParams
) => {
  return useQuery({
    queryKey: galleryKeys.filter(params, filters),
    queryFn: () => galleryService.filterGalleries(params, filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

/**
 * Hook to get home page highlights
 */
export const useGalleryHighlights = () => {
  return useQuery({
    queryKey: [...galleryKeys.all, 'highlights', 'home'],
    queryFn: () => galleryService.getHomePageHighlights(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

